import PhysicalLocation from "domain/physicalLocation"
import useMerchantFeature from "features/merchants/useMerchantFeature";
import { MerchantFeature } from "domain/merchant";
import useStoreSelector from "common/redux/useStoreSelector";
import { useAllowedLocations } from "../useLocations";

interface MappedLocationsHookResult {
    getDefault(deliveryLocationId: Nullish<string>, inventoryLocationId?: Nullish<string>): DefaultLocationMapping;
    getOptions(deliveryLocationId: Nullish<string>, inventoryLocationId?: Nullish<string>): MappedLocationOptions;
}

type MappedLocationOptions = {
    deliveryLocations: PhysicalLocation[];
    inventoryLocations: PhysicalLocation[];
    contractLocations: PhysicalLocation[];
}

type DefaultLocationMapping = {
    deliveryLocation: PhysicalLocation;
    inventoryLocation: PhysicalLocation;
    contractLocation: PhysicalLocation;
}

export default function useMappedLocations(): MappedLocationsHookResult {

    const restrictLocations = useStoreSelector(m => m.userState.activeMerchant.restrictLocationsOnContractsToMappings);
    const areTypesEnabled = useMerchantFeature(MerchantFeature.LocationTypes);
    const allowedLocations = useAllowedLocations();
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);

    const locationMappings = useStoreSelector(m => m.locationMappings);

    const filteredLocations = {
        deliveryLocations: allowedLocations.filter(m => m.isDeliveryLocation),
        inventoryLocations: allowedLocations.filter(m => m.isInventoryLocation),
        contractLocations: allowedLocations.filter(m => m.isContractLocation)
    };

    function getDefault(deliveryLocationId: Nullish<string>, inventoryLocationId?: Nullish<string>): DefaultLocationMapping {
        const mapping = locationMappings?.find(mapping => mapping.deliveryLocationId === deliveryLocationId && (inventoryLocationId == null || mapping.inventoryLocationId === inventoryLocationId));

        const defaults: DefaultLocationMapping = {
            deliveryLocation: filteredLocations.deliveryLocations.find(location => location.id === deliveryLocationId) ?? null,
            inventoryLocation: filteredLocations.inventoryLocations.find(location => location.id === mapping?.inventoryLocationId) ?? null,
            contractLocation: filteredLocations.contractLocations.find(location => location.id === mapping?.contractLocationId) ?? null
        }

        return defaults;
    }

    function getOptions(deliveryLocationId: Nullish<string>, inventoryLocationId?: Nullish<string>): MappedLocationOptions {
        return forLocation(deliveryLocationId, inventoryLocationId, restrictLocations);
    }

    function forLocation(deliveryLocationId: string, inventoryLocationId: string, restrictLocations: boolean): MappedLocationOptions {
        if (!restrictLocations || isCustomer || !areTypesEnabled) {
            return filteredLocations;
        }

        const matchedMappings = locationMappings.filter(m =>
            m.deliveryLocationId === deliveryLocationId &&
            (inventoryLocationId == null || m.inventoryLocationId === inventoryLocationId));

        return {
            ...filteredLocations,
            inventoryLocations: filteredLocations.inventoryLocations.filter(l => matchedMappings.some(map => map.inventoryLocationId === l.id)),
            contractLocations: filteredLocations.contractLocations.filter(l => matchedMappings.some(map => map.contractLocationId === l.id)),
        }
    }

    return {
        getDefault,
        getOptions
    }

}